import { createRef, useCallback, useMemo, useState } from 'react'

import { isPrivacyCenterContentLoading } from 'common/query-screen-status'
import { useNavigate } from 'react-router-dom'
import { Routes } from 'common/constants'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { TSignalType } from 'common/types'
import { useHasScroll } from 'common/hooks/useHasScroll'
import { logBridgeError, sendSelectContent } from 'common/helpers'

import { MyServiceFilters, TServiceItems } from '../types/MyServices.types'
import { useGetServices } from '../repository/MyService.repository'
import { filterServices, getChips, prepareServices } from '../services/MyServices.services'
import { baseState } from '../utils/constants'

export const useMyServiceContent = () => {
  const { services, isLoading: isServicesLoading, isServicesError, refetch } = useGetServices()
  const { privacyCenterContent, userSelectedEntity, isAppLoading, isAppError } = useAppContext()

  const ref = createRef<HTMLDivElement>()
  const hasScroll = useHasScroll(ref)
  const navigate = useNavigate()

  const [selectedFilters, setSelectedFilters] = useState<MyServiceFilters>({})
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)

  const supportData = privacyCenterContent?.components.supportData
  const filterDictionary = supportData?.filterDictionary
  const mySupports = privacyCenterContent?.components.mySupports
  const subjectLabel =
    privacyCenterContent?.components.newSupport?.content.subjects.title ?? 'Assunto'
  const isActive = !!mySupports?.isActive
  const hasContent = !!mySupports?.content
  const isContentLoading = isPrivacyCenterContentLoading(isAppLoading, hasContent)
  const isLoading = isContentLoading || isServicesLoading

  const baseServices: TServiceItems[] = useMemo(
    () => prepareServices(services, filterDictionary),
    [services, filterDictionary],
  )
  const isEmpty = baseServices.length === 0
  const filteredServices = useMemo(
    () =>
      filterServices({
        filterDictionary,
        filters: selectedFilters,
        items: baseServices,
      }),
    [baseServices, filterDictionary, selectedFilters],
  )
  const activeFilter = Object.values(selectedFilters).join(', ')
  const filterSections = useMemo(
    () =>
      [
        {
          id: 'status',
          label: 'Status',
          options: getChips(filterDictionary),
          defaultOption: filterDictionary?.ALL,
        },
        {
          id: 'subject',
          label: subjectLabel,
          options: supportData?.subjectList.map((subject) => subject.title) ?? [],
        },
      ].filter((section) => section.options.length > 0),
    [filterDictionary, subjectLabel, supportData],
  )

  const emptyFilter = useMemo(() => {
    if (!mySupports) return baseState.emptyFilter

    return {
      ...mySupports.content.emptyFilter,
      title: mySupports.content.emptyFilter.title.replace(':status', activeFilter),
      type: 'gray' as TSignalType,
    }
  }, [mySupports, activeFilter])

  const emptyRequest = useMemo(() => {
    if (!mySupports) return baseState.emptyRequest

    return {
      ...mySupports?.content.emptyRequest,
      type: 'gray' as TSignalType,
    }
  }, [mySupports])

  const isError = useMemo(
    () => !isLoading && (isAppError || isServicesError || !hasContent || !isActive),
    [hasContent, isActive, isAppError, isLoading, isServicesError],
  )

  useScreenFinishLoadTac({
    name: 'MyServices',
    screen: Routes.MY_SERVICE_REQUESTS,
    action: 'MyServicesFinishLoad',
    entity: userSelectedEntity,
    shouldSend: !isLoading && !isError && !!mySupports?.content,
    logLabel: 'send my services finish tac',
  })

  const onClick = async () => {
    try {
      await sendSelectContent({
        flow: 'my_services',
        step: 'my_service_requests',
        screen_name: Routes.MY_SERVICE_REQUESTS,
        element_name: 'goToNewService',
        element_type: 'button',
        parent_name: 'MyServices',
        scenario: JSON.stringify({
          screen: Routes.MY_SERVICE_REQUESTS,
          product: userSelectedEntity,
          isFirstRequest: `${isEmpty}`,
          ref_type: 'button',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send my services tic', error)
    }

    navigate(Routes.NEW_SERVICE)
  }

  const onApplyFilters = useCallback(
    async (nextFilters: MyServiceFilters) => {
      try {
        await sendSelectContent({
          flow: 'my_services',
          step: 'my_service_requests',
          screen_name: Routes.MY_SERVICE_REQUESTS,
          element_name: 'changeFilterChip',
          element_type: 'button',
          parent_name: 'MyServices',
          scenario: JSON.stringify({
            screen: Routes.MY_SERVICE_REQUESTS,
            product: userSelectedEntity,
            selectedFilters: nextFilters,
            ref_type: 'button',
            content_action: 'touch',
          }),
        })
      } catch (error: unknown) {
        logBridgeError('send my services filter tic', error)
      }

      setSelectedFilters(nextFilters)
    },
    [userSelectedEntity],
  )

  const onClearFilter = useCallback((filterId: string) => {
    setSelectedFilters((currentFilters) => {
      const nextFilters = { ...currentFilters }

      if (filterId === 'status') delete nextFilters.status
      if (filterId === 'subject') delete nextFilters.subject

      return nextFilters
    })
  }, [])

  const onBottomSheetOpenChange = useCallback((isOpen: boolean) => {
    setIsBottomSheetOpen(isOpen)
  }, [])

  const statusFilterSection = filterSections.find((section) => section.id === 'status')
  const selectedStatus = selectedFilters.status ?? statusFilterSection?.defaultOption
  const onStatusFilter = useCallback(
    (selectedOption: string) => {
      const nextFilters = { ...selectedFilters }

      if (selectedOption === statusFilterSection?.defaultOption) {
        delete nextFilters.status
      } else {
        nextFilters.status = selectedOption
      }

      onApplyFilters(nextFilters)
    },
    [onApplyFilters, selectedFilters, statusFilterSection?.defaultOption],
  )

  return {
    ...baseState,
    ...mySupports?.content,
    hasScroll,
    isError,
    filterSections,
    selectedFilters,
    filteredServices,
    baseServices,
    isEmptyFilter: filteredServices.length <= 0,
    emptyRequest,
    emptyFilter,
    isEmpty,
    isLoading,
    ref,
    isActive,
    content: mySupports?.content,
    onApplyFilters,
    onClearFilter,
    onBottomSheetOpenChange,
    isBottomSheetOpen,
    chipFilterProps: {
      options: statusFilterSection?.options ?? [],
      selectedOption: selectedStatus,
      onFilter: onStatusFilter,
    },
    chipBottomSheetProps: {
      sections: filterSections,
      selectedFilters,
      onApply: onApplyFilters,
      onClear: onClearFilter,
      onOpenChange: onBottomSheetOpenChange,
    },
    onClick,
    refetch,
  }
}
